/**
 * Wall-clock ↔ instant conversions in a NAMED zone — the campaign's Schedule pick — rather than the
 * browser's. The builder's dates are read server-side in that zone (`combineDateTime` in
 * gfgf-api's campaign-builder-serializer), so anything the builder shows or checks before a save
 * has to use the same one, or a sender in Central who picked Eastern sees every time an hour off.
 */

/** The builder's Schedule codes → IANA zones. Same table as gfgf-api's `SCHEDULE_TIMEZONES`. */
const SCHEDULE_TIMEZONES: Record<string, string> = {
  ET: "America/New_York",
  CT: "America/Chicago",
  MT: "America/Denver",
  PT: "America/Los_Angeles",
};

/** The IANA zone for a Schedule code; anything else is Eastern, the picker's own default. */
export function resolveScheduleTimeZone(code?: string | null): string {
  if (!code) return "America/New_York";
  if (Object.hasOwn(SCHEDULE_TIMEZONES, code)) return SCHEDULE_TIMEZONES[code] as string;
  return Object.values(SCHEDULE_TIMEZONES).includes(code) ? code : "America/New_York";
}

/** The builder's zone for the active mode: the link-activation pick for self-send campaigns (manual +
 *  universal), else the schedule pick. */
export function resolveCampaignTimeZone(s: {
  notificationMode?: string;
  scheduleTz?: string | null;
  activationTz?: string | null;
}): string {
  const usesActivation = s.notificationMode === "manual" || s.notificationMode === "universal";
  return resolveScheduleTimeZone(usesActivation ? s.activationTz : s.scheduleTz);
}

const ZONE_NAMES: Record<string, string> = {
  ET: "Eastern",
  CT: "Central",
  MT: "Mountain",
  PT: "Pacific",
};

/** The Schedule code ("PT") of an IANA zone; anything else is Eastern, as in the picker. */
export function scheduleZoneCode(timeZone: string): string {
  return Object.keys(SCHEDULE_TIMEZONES).find((c) => SCHEDULE_TIMEZONES[c] === timeZone) ?? "ET";
}

/** The zone's plain name ("Pacific"). */
export function scheduleZoneName(timeZone: string): string {
  return ZONE_NAMES[scheduleZoneCode(timeZone)] as string;
}

export function scheduleZoneLabel(timeZone: string): string {
  return `${scheduleZoneName(timeZone)} (${scheduleZoneCode(timeZone)})`;
}

const FORMATTERS = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = FORMATTERS.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    FORMATTERS.set(timeZone, formatter);
  }
  return formatter;
}

function zonedParts(at: Date, timeZone: string) {
  const p: Record<string, number> = {};
  for (const part of partsFormatter(timeZone).formatToParts(at)) {
    if (part.type !== "literal") p[part.type] = Number(part.value);
  }
  return p as Record<"year" | "month" | "day" | "hour" | "minute" | "second", number>;
}

function zoneOffsetMs(at: Date, timeZone: string): number {
  const p = zonedParts(at, timeZone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - at.getTime();
}

/** "YYYY-MM-DD" + "HH:mm" read in `timeZone` → the ISO instant it names; null when unusable. */
export function zonedWallTimeToIso(date: string, time: string, timeZone: string): string | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const t = /^(\d{2}):(\d{2})$/.exec(time);
  if (!d || !t) return null;
  const [year, month, day, hour, minute] = [d[1], d[2], d[3], t[1], t[2]].map(Number) as [
    number,
    number,
    number,
    number,
    number,
  ];
  const guess = new Date(Date.UTC(year, month - 1, day, hour, minute));
  if (guess.getUTCMonth() !== month - 1 || guess.getUTCDate() !== day || hour > 23 || minute > 59) {
    return null;
  }
  const first = new Date(guess.getTime() - zoneOffsetMs(guess, timeZone));
  const result = new Date(guess.getTime() - zoneOffsetMs(first, timeZone));
  // A spring-forward gap time (e.g. 02:30) doesn't exist: read it forward (03:30), as Temporal does.
  const p = zonedParts(result, timeZone);
  const exists = p.day === day && p.hour === hour && p.minute === minute;
  return (exists ? result : first).toISOString();
}

/** The inverse: an instant → the `YYYY-MM-DD` / `HH:mm` a clock in `timeZone` showed at it. */
export function isoToZonedSlot(iso: string, timeZone: string): { date: string; time: string } {
  const p = zonedParts(new Date(iso), timeZone);
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${p.year}-${pad(p.month)}-${pad(p.day)}`,
    time: `${pad(p.hour)}:${pad(p.minute)}`,
  };
}
